import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { applyReferralCode } from "@/lib/referrals.functions";

const KEY = "behot_ref";

export function storedRefCode() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Captures ?ref=CODE from the current URL and applies it once the user signs in. */
export function useReferralCapture(fallbackCode?: string | null) {
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // A creator page attributes signups to its owner even without ?ref=,
    // an explicit ?ref= in the URL always wins.
    const code = new URLSearchParams(window.location.search).get("ref") ?? fallbackCode ?? null;
    if (code && /^[A-Za-z0-9_-]{3,32}$/.test(code)) {
      try {
        window.localStorage.setItem(KEY, code);
      } catch {
        /* storage blocked */
      }
    }
  }, [fallbackCode]);

  useEffect(() => {
    if (!user) return;
    const code = storedRefCode();
    if (!code) return;
    void (async () => {
      try {
        await applyReferralCode({ data: { code } });
        window.localStorage.removeItem(KEY);
      } catch {
        /* retried on next sign-in */
      }
    })();
  }, [user]);
}

/** The signed-in user's own referral code, for share links. */
export function useMyReferralCode() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["my-referral-code", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_code")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data?.referral_code ?? null;
    },
  });
  return data ?? null;
}
